import { useContext, useEffect, useRef } from 'react';
import { Refresh } from '@mui/icons-material';
import { Button, Grid2 as Grid, Typography } from '@mui/material';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { GridSkeleton } from 'components/Grid';
import { LayoutContext } from 'context';
import { LogsFilter, LogsTable } from './components';
import { FILTER_BLOCKS_SIZE } from './constants';
import { useAuditLogs } from './hooks';

const AuditLogs = () => {
  const { setPageTitle } = useContext(LayoutContext);
  const [store] = useStore() as unknown as UseStoreValues; // Ensure the store is initialized
  const { user } = store;

  const userId = user?.id ?? null;
  const tableRef = useRef<HTMLDivElement>(null);

  const {
    logsScope,
    logs,
    refreshLogs,
    loadMore,
    hasNextPage,
    isRequesting,
    hasAccess,
    handleBusinessEntitySelection,
    handleDateRangeSelection,
    queryParams,
  } = useAuditLogs(userId, tableRef);

  useEffect(() => {
    setPageTitle?.('Audit Logs');
  }, [setPageTitle]);

  if (!logsScope) {
    return <GridSkeleton />;
  }

  if ((logsScope.firms.length === 0 && logsScope.standalone_companies.length === 0) || !hasAccess) {
    return (
      <Typography variant="h6" sx={{ mt: 4 }}>
        You do not have access to view audit logs. Contact your administrator for more information.
      </Typography>
    );
  }

  return (
    <>
      <Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
        <LogsFilter
          scope={logsScope}
          handleBusinessEntitySelection={handleBusinessEntitySelection}
          handleDateRangeSelection={handleDateRangeSelection}
        />
        <Grid>
          <Button
            startIcon={<Refresh />}
            onClick={() => (queryParams ? refreshLogs(queryParams) : refreshLogs())}
            variant="contained"
            loading={isRequesting}
            sx={{ height: FILTER_BLOCKS_SIZE }}>
            Refresh
          </Button>
        </Grid>
      </Grid>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
        Viewing {(logs ?? []).length} log{(logs ?? []).length === 1 ? '' : 's'} in total
      </Typography>
      <LogsTable
        ref={tableRef}
        logs={logs}
        loadMore={loadMore}
        isLoadingMore={isRequesting}
        hasNextPage={hasNextPage}
      />
    </>
  );
};

export default AuditLogs;
